import { action, computed, makeObservable, observable } from 'mobx';
import { ItemWithLabel } from '../../../../components';
import { serialize, unserialize } from '../../../../store/utils/localstorage';
import Sellable, { SellableDto } from './Sellable';

export default abstract class Sellables<T extends Sellable> {
  items: T[] = [];
  nameFilter = '';
  stateFilter: StateFilter = 'published';

  itemName = 'sellable';

  constructor(
    sellableDtos: SellableDto[],
    private readonly onSave?: (sellable: T) => Promise<void>,
    private readonly persistentFilter = false,
  ) {
    makeObservable(this, {
      items: observable,
      nameFilter: observable,
      stateFilter: observable,
      isEmpty: computed,
      filteredItems: computed,
      setNameFilter: action.bound,
      setStateFilter: action.bound,
    });

    this.items = sellableDtos.map((dto) => this.unserialize(dto));

    if (this.persistentFilter) {
      this.nameFilter = unserialize(`${this.itemName}NameFilter`, '');
    }
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  get activeItems(): this {
    return this.cloneFiltered((p) => !p.archived).sortByName();
  }

  get filteredItems(): T[] {
    const nameFilter = this.nameFilter.trim().toLowerCase();

    return this.items.filter((sellable) =>
      isItemMatchFilter(sellable, nameFilter, this.stateFilter),
    );
  }

  clone(): this {
    const Constructor = (this as any).constructor;
    const clone = new Constructor([], this.onSave, this.persistentFilter);
    clone.items = this.items;
    return clone;
  }

  cloneFiltered(filter: (sellable: T) => boolean): this {
    const clone = this.clone();
    clone.items = this.items.filter((item) => filter(item));
    return clone;
  }

  getTagOptions(): ItemWithLabel[] {
    const tags = [];
    for (const sellable of this.items) {
      const itemTags = sellable.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
        .toSorted((a, b) => a.localeCompare(b));
      tags.push(...itemTags);
    }

    return tags.map((tag) => ({ value: tag, label: tag }));
  }

  getById(sellableId: string) {
    return this.items.find((i) => i.id === sellableId);
  }

  setNameFilter(value: string) {
    this.nameFilter = value;

    if (this.persistentFilter) {
      serialize(`${this.itemName}NameFilter`, value);
    }
  }

  setStateFilter(state: StateFilter) {
    this.stateFilter = state;
  }

  sortByName(): this {
    const clone = this.clone();
    clone.items.sort((a, b) => a.name.localeCompare(b.name));
    return clone;
  }

  protected abstract unserialize(dto: SellableDto): T;
}

function isItemMatchFilter(
  item: Sellable,
  nameFilter: string,
  stateFilter: StateFilter,
): boolean {
  if (
    nameFilter &&
    !(
      item.displayName.toLowerCase().includes(nameFilter) ||
      item.tags.toLowerCase().includes(nameFilter)
    )
  ) {
    return false;
  }

  switch (stateFilter) {
    case 'archived': {
      return item.archived;
    }
    case 'published': {
      return !item.archived;
    }
    default: {
      return true;
    }
  }
}

export type StateFilter = 'archived' | 'published';
